import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { getConfig } from "../utils/config";
import { getStoryReadyTime } from "../utils/storyReadyTime";
import { getStoryUrl } from "../utils/storyUrl";
import { getBookingUrl } from "../utils/bookingUrl";
import { trackEvent } from "../utils/analytics";
import { formatStoryCountdown, capitalize } from "../helpers";
import "./SessionCompleted.scss";
import getStoryData from "../utils/storyData";
import { StoryData, PlayerItem } from "../types/story";
import defaultAvatar from "../assets/img/boy.png";

interface LeaderboardEntry {
  name: string;
  photo?: string | null;
  points: number;
}

interface PlayerRowProps {
  player: LeaderboardEntry;
  index: number;
  score: number;
  isTournament: boolean;
  isTeams: boolean;
}

const PlayerRow: React.FC<PlayerRowProps> = ({ player, index, score, isTournament, isTeams }) => (
  <div className={`lbRow${index < 3 ? ` lbRowRank${index + 1}` : ""}`}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="39"
      height="47"
      viewBox="0 0 39 47"
      fill="none"
      className="lbRowRankDiamond"
    >
      <path
        d="M37.4473 23.25L19.375 44.9365L1.30176 23.25L19.375 1.5625L37.4473 23.25Z"
        stroke="#D8B464"
        strokeWidth="2"
      />
      <text x="19.375" y="23.25" textAnchor="middle" dominantBaseline="central" className="lbRowRankNum">
        {index + 1}
      </text>
    </svg>
    {!isTournament && !isTeams && (
      <div className="lbRowAvatarWrap">
        <img
          className="lbRowAvatar"
          src={player.photo ? `data:image/jpeg;base64, ${player.photo}` : defaultAvatar}
          alt={player.name}
        />
      </div>
    )}
    <span className="name">{capitalize(player.name)}</span>
    <span className="points">
      {score} <span>PTS</span>
    </span>
  </div>
);

const RE_VARIANT_VALUE = "A_leaderboard";

interface SessionCompletedAProps {
  isTestVariant?: boolean;
}

export const SessionCompletedA: React.FC<SessionCompletedAProps> = ({ isTestVariant = false }) => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("s");
  const sessionEndTime = searchParams.get("d");
  const storyReadyTime = sessionEndTime ? getStoryReadyTime(new Date(sessionEndTime)) : null;
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [storyData, setStoryData] = useState<StoryData | null>(null);

  const canViewStory = storyReadyTime ? currentTime >= storyReadyTime : false;

  const [storyUrl, setStoryUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    getStoryData(sessionId).then(setStoryData);
  }, [sessionId]);

  useEffect(() => {
    if (!isTestVariant) return;
    trackEvent({
      gaEvent: "rapid_entry_test_impression",
      params: { re_variant: RE_VARIANT_VALUE },
    });
  }, [isTestVariant]);

  const ctaVariantSuffix = isTestVariant ? `&re_variant=${RE_VARIANT_VALUE}` : "";

  const isTournament = storyData?.type === "1" || storyData?.type === "3";
  const isTeams = storyData?.type === "1";

  // final_result 1-6 are explicit ranks; everything else (or if none set) is sorted by points.
  const buildRankedList = (players: PlayerItem[]) => {
    const ranked = players
      .filter((i) => Number(i.final_result) >= 1 && Number(i.final_result) <= 6)
      .sort((a, b) => Number(a.final_result) - Number(b.final_result));
    const unranked = players
      .filter((i) => !(Number(i.final_result) >= 1 && Number(i.final_result) <= 6))
      .sort((a, b) => (b.points || 0) - (a.points || 0));
    return ranked.length === 0 ? unranked : [...ranked, ...unranked];
  };

  // type 1: teams ranked by points. type 3: players ranked by final_result. other: players ranked by points.
  const leaderboardEntries = useMemo<LeaderboardEntry[]>(() => {
    if (isTeams) {
      return [...(storyData?.Teams ?? [])]
        .sort((a, b) => (b.points || 0) - (a.points || 0))
        .map((team) => ({ name: team.name, points: team.points || 0 }));
    }
    const players = storyData?.Players ?? [];

    const ranked = isTournament
      ? buildRankedList(players)
      : [...players].sort((a, b) => (b.points || 0) - (a.points || 0));
    return ranked.map((player) => ({ name: player.name, photo: player.photo, points: player.points || 0 }));
  }, [storyData, isTeams, isTournament]);

  useEffect(() => {
    if (!storyReadyTime || canViewStory) {
      return;
    }

    const intervalId = window.setInterval(() => setCurrentTime(new Date()), 1000);
    return () => window.clearInterval(intervalId);
  }, [storyReadyTime, canViewStory]);

  useEffect(() => {
    if (canViewStory) {
      getConfig()
        .then((config) => {
          setStoryUrl(getStoryUrl(config.storyRegionDomain, sessionId));
        })
        .catch(() => {}); // fall back to "story not ready" copy
    }
  }, [canViewStory, sessionId]);

  const storyCountdown = storyReadyTime
    ? formatStoryCountdown(Math.max(0, storyReadyTime.getTime() - currentTime.getTime()))
    : null;

  return (
    <div className="pageContainer balloons-bg sessionCompletedPage">
      <div className="balloon balloon--green">
        <img src="./img/fc-balloon-green-gold.svg" alt="" />
      </div>
      <div className="balloon balloon--orange">
        <img src="./img/fc-balloon-orange-cream.webp" alt="" />
      </div>
      <div className="balloon balloon--cream">
        <img src="./img/fc-balloon-cream-red.svg" alt="" />
      </div>
      <div className="balloon balloon--purple">
        <img src="./img/fc-balloon-purple-pink.svg" alt="" />
      </div>
      <div className="logoContainer">
        <img src={"./img/fc-logo.svg"} width="125px" alt="Flight Club" />
      </div>
      <div className="contentCard-outer">
        <div className="contentCard contentCard--complete completeSession">
          <img
            src="./img/well-played.svg"
            alt="Well Played"
            className={`wellPlayedImg ${storyUrl ? "wellPlayedImg--storyReady" : ""}`}
          />
          {!storyUrl && (
            <>
              <div className="sessionCompletedText">
                We're putting together the best bits from your game in your own highlights reel. Check back
                when the countdown ends to see it!
                {storyCountdown && (
                  <span className="storyCountdown" aria-live="polite">
                    {storyCountdown}
                  </span>
                )}
              </div>
              <img src="./img/fc-action-replay.gif" alt="Action Replay" className="actionReplayGif" />
            </>
          )}
          {storyUrl && (
            <>
              <div className="sessionCompletedText">
                Meet the leaderboard - then watch the game highlights in your Story.
              </div>
              <div className="leaderboard">
                {leaderboardEntries.slice(0, 3).map((entry, index) => (
                  <PlayerRow
                    key={index}
                    player={entry}
                    score={entry.points}
                    index={index}
                    isTournament={isTournament}
                    isTeams={isTeams}
                  />
                ))}
              </div>
              <div className="storyButtons">
                <a
                  className="small viewStoryLink"
                  href={`${storyUrl}?utm_source=rapid_entry&utm_medium=session_complete_story${ctaVariantSuffix}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackEvent({
                      gaEvent: "session_complete_story",
                      clarityEvent: "session_complete_story",
                    });
                  }}
                >
                  Enjoy your story
                </a>
                <a
                  className="small white"
                  href={`${getBookingUrl()}/?utm_source=rapid_entry&utm_medium=session_complete${ctaVariantSuffix}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackEvent({
                      gaEvent: "session_complete_book",
                      clarityEvent: "session_complete_book",
                    });
                  }}
                >
                  Book again
                </a>
              </div>
            </>
          )}
        </div>
      </div>
      {!storyUrl && (
        <footer className="btn-container">
          <a
            className="small"
            href={`${getBookingUrl()}/?utm_source=rapid_entry&utm_medium=session_complete${ctaVariantSuffix}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent({
                gaEvent: "session_complete_book",
                clarityEvent: "session_complete_book",
              });
            }}
          >
            Book again
          </a>
        </footer>
      )}
    </div>
  );
};
